import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhMitreApi } from 'src/api/mindtime/wazuhMitreApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhMitreEndpointItem,
  WazuhMitreQuery,
  WazuhMitreSummaryData,
  WazuhMitreTechniqueItem,
} from 'src/types/mindtime/wazuhMitre';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type MitreFacets = {
  tactics: string[];
  techniques: string[];
  mitreIds: string[];
};

export type MitreFilters = {
  tactic?: string;
  technique?: string;
  mitreId?: string;
  severityBand?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type MitreSortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type MitreState = {
  loading: boolean;
  summary: WazuhMitreSummaryData | null;
  items: WazuhPagedData<WazuhMitreTechniqueItem> | null;
  endpoints: WazuhPagedData<WazuhMitreEndpointItem> | null;
  topTechniques: WazuhMitreTechniqueItem[];
  facets: MitreFacets;
};

type BuildMitreQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: MitreFilters;
  page?: number;
  pageSize?: number;
  sortParams?: MitreSortParams | null;
};

const emptyFacets = (): MitreFacets => ({
  tactics: [],
  techniques: [],
  mitreIds: [],
});

const emptyState = (): MitreState => ({
  loading: true,
  summary: null,
  items: null,
  endpoints: null,
  topTechniques: [],
  facets: emptyFacets(),
});

const mergeFacets = (
  items: WazuhMitreTechniqueItem[],
  previous: MitreFacets
): MitreFacets => {
  const tactics = new Set(previous.tactics);
  const techniques = new Set(previous.techniques);
  const mitreIds = new Set(previous.mitreIds);

  items.forEach((item) => {
    if (item.tactic) tactics.add(item.tactic);
    if (item.techniqueName) techniques.add(item.techniqueName);
    if (item.mitreId) mitreIds.add(item.mitreId);
  });

  return {
    tactics: Array.from(tactics).sort((a, b) => a.localeCompare(b)),
    techniques: Array.from(techniques).sort((a, b) => a.localeCompare(b)),
    mitreIds: Array.from(mitreIds).sort((a, b) => a.localeCompare(b)),
  };
};

const buildMitreQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  selectedEndpointId,
  page,
  pageSize,
  sortParams,
}: BuildMitreQueryInput & { selectedEndpointId?: string | null }): WazuhMitreQuery => {
  const { tactic, technique, mitreId, severityBand, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(tactic ? { tactic } : {}),
    ...(technique ? { technique } : {}),
    ...(mitreId ? { mitreId } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
    ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useMitreExport = (search = '', filters: MitreFilters = {}) => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode } = useEdrDataGate('mitre-attck');
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhMitreApi
      .export(
        buildMitreQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          selectedEndpointId,
        })
      )
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'mitre_techniques_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useMitreExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters, selectedEndpointId, isPreviewMode]);

  return { exporting, handleExport };
};

export const useMitreData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: MitreFilters = {},
  endpointsSortParams: MitreSortParams | null = null
): MitreState => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('mitre-attck');
  const { canWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'mitre-attck',
  );
  const canSummary = canWidget('summary');
  const canTechniquesList = canWidget('techniquesList');
  const canTopTechniques = canWidget('topTechniques');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<MitreState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
        organizationId: filters.organizationId || '',
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller, filters.organizationId]
  );

  const { tactic, technique, mitreId, severityBand, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId || '';

  useEffect(() => {
    setState((prev) => ({ ...prev, facets: emptyFacets() }));
  }, [scopeKey]);

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as MitreState);
      return undefined;
    }

    if (!canSummary && !canTopTechniques) {
      setState((prev) => ({ ...prev, summary: null, topTechniques: [] }));
      return undefined;
    }

    let cancelled = false;

    const effectiveScope = buildEdrApiScope({
      scope,
      profileOrgId,
      isReseller,
      pageOrganizationId: organizationId || undefined,
    });

    const fetches: Promise<void>[] = [];

    if (canSummary) {
      fetches.push(
        mindtimeWazuhMitreApi.getSummary(effectiveScope).then((summary) => {
          if (!cancelled) setState((prev) => ({ ...prev, summary }));
        }),
      );
    } else {
      setState((prev) => ({ ...prev, summary: null }));
    }

    if (canTopTechniques) {
      fetches.push(
        mindtimeWazuhMitreApi.getTopTechniques({ ...effectiveScope, limit: 5 }).then((topTechniques) => {
          if (!cancelled) setState((prev) => ({ ...prev, topTechniques }));
        }),
      );
    } else {
      setState((prev) => ({ ...prev, topTechniques: [] }));
    }

    Promise.all(fetches).catch((error) => {
      if (!cancelled) {
        logger(error, 'error', 'useMitreData.summary');
        setState((prev) => ({
          ...prev,
          summary: canSummary ? null : prev.summary,
          topTechniques: canTopTechniques ? [] : prev.topTechniques,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    organizationId,
    isPreviewMode,
    wait,
    getPreviewState,
    canSummary,
    canTopTechniques,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canTechniquesList) {
      setState((prev) => ({ ...prev, loading: false, items: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    mindtimeWazuhMitreApi
      .getTechniques(
        buildMitreQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page,
          pageSize,
          selectedEndpointId,
        })
      )
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
          facets: mergeFacets(items.items, prev.facets),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMitreData.techniques');
          setState((prev) => ({
            ...prev,
            loading: false,
            items: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    tactic,
    technique,
    mitreId,
    severityBand,
    wazuhAgentId,
    organizationId,
    isPreviewMode,
    skipLive,
    canTechniquesList,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canAffectedEndpoints) {
      setState((prev) => ({ ...prev, endpoints: null }));
      return undefined;
    }

    let cancelled = false;
    const sortParams = endpointsSortParams ?? { column: 'totalDetections', sort: 'desc' as const };

    mindtimeWazuhMitreApi
      .listEndpoints(
        buildMitreQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page: 0,
          pageSize,
          sortParams,
          selectedEndpointId,
        })
      )
      .then((endpoints) => {
        if (!cancelled) setState((prev) => ({ ...prev, endpoints }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMitreData.endpoints');
          setState((prev) => ({ ...prev, endpoints: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    pageSize,
    search,
    tactic,
    technique,
    mitreId,
    severityBand,
    wazuhAgentId,
    organizationId,
    endpointsSortParams?.column,
    endpointsSortParams?.sort,
    isPreviewMode,
    skipLive,
    canAffectedEndpoints,
  ]);

  return state;
};
